import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import type { Environment } from "../../../types";
import type { UserDepositInfo, UserDepositsResponse } from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const USER_DEPOSITS_QUERY_KEY = "wp:user-deposits";

export function useUserDeposits(
  env: Environment,
  walletAddress: string,
  enabled = true,
) {
  const isEnabled = enabled && !!walletAddress;

  const { data, ...rest } = useQuery<UserDepositsResponse>({
    queryKey: [USER_DEPOSITS_QUERY_KEY, env, walletAddress],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("gameReward", env);
      const res = await fetch(
        `${baseUrl}/v1/users/${walletAddress}/deposits`,
        { cache: "no-store", signal },
      );
      if (!res.ok) throw new Error(`Deposits fetch failed: ${res.status}`);
      const json: UserDepositsResponse = await res.json();
      if (json.code !== 200) throw new Error(`Deposits API error: ${json.code}`);
      return json;
    },
    enabled: isEnabled,
    staleTime: 0,
  });

  const depositMap = useMemo(() => {
    const map = new Map<string, UserDepositInfo>();
    if (!data?.data.user_deposits) return map;
    for (const deposit of data.data.user_deposits) {
      map.set(deposit.pool_address.toLowerCase(), deposit);
    }
    return map;
  }, [data]);

  return { depositMap, deposits: data?.data.user_deposits ?? [], ...rest };
}
